import { useCallback } from "react";
import { QueryConstraint, where } from "firebase/firestore";

import { COLLECTION_ID } from "@highnote/server/src/core/entities";
import { getSpaceTrackRowId } from "@highnote/server/src/core/shared-util";
import { useGlobalSpaces } from "../spaces/useGlobalSpaces";
import { useGlobalTracks } from "../tracks/useGlobalTracks";
import { useGlobalSpaceTracks } from "../spaceTracks/useGlobalSpaceTracks";
import {
  SpaceTrackTile,
  buildSpaceTrackTile,
} from "../tracks/buildSpaceTrackTile";
import { WatcherComponentId } from "../watchers/useWatcherV2";
import { asViewingSpaceId } from "App/modules/viewingSpace";

/**
 * Fetches all child spaces and tracks of the space.
 * Controlled by an expanded row state, or the mounted component it's initialized in.
 */
export const useChildEntityWatchers = () => {
  const { manageGlobalMultipleSpacesWatcher, globalSpaces } = useGlobalSpaces();
  const { manageGlobalTracksWatchers, globalTracksDispatch, globalTracks } =
    useGlobalTracks();
  const {
    manageGlobalSpaceTracksWatchers,
    globalSpaceTracksDispatch,
    globalSpaceTracks,
  } = useGlobalSpaceTracks();

  const setGlobalChildEntityWatchers = ({
    collectionId,
    action,
    parentSpaceId,
    constraints,
    limit,
    componentId,
  }: {
    collectionId: COLLECTION_ID;
    action: "attach" | "detach";
    parentSpaceId: string;
    constraints?: QueryConstraint[];
    limit?: number;
    componentId: WatcherComponentId;
  }) => {
    if (collectionId === COLLECTION_ID.SPACE) {
      return manageGlobalMultipleSpacesWatcher({
        action,
        watcherKey: parentSpaceId,
        constraints,
        limit,
        componentId,
      });
    }

    if (collectionId === COLLECTION_ID.TRACK) {
      return manageGlobalTracksWatchers({
        action,
        watcherKey: parentSpaceId,
        componentId,
        constraints,
        limit,
        setEntities: (entities) => {
          globalTracksDispatch({
            type: "ADD_CHILD_TRACKS",
            payload: {
              parentSpaceId,
              entities: entities,
            },
          });
        },
      });
    }

    if (collectionId === COLLECTION_ID.SPACE_TRACK) {
      return manageGlobalSpaceTracksWatchers({
        action,
        watcherKey: parentSpaceId,
        componentId,
        constraints,
        limit,
        setEntities: (rows) => {
          globalSpaceTracksDispatch({
            type: "SET_SPACE_TRACK_ROWS",
            payload: {
              spaceId: parentSpaceId,
              rows,
            },
          });
        },
      });
    }
  };

  /**
   * The query constraints for one collection in a child-entity watch.
   *
   * `space-tracks-v1` is NOT free-form. Its security rule reflects the
   * query's own filters (a `list` rule never sees documents), and it accepts
   * exactly ONE shape: a single equality on `spaceId`. OR, `!=`, ranges and
   * multi-value `IN` all DENY — and a denied `list` returns ALLOW with zero
   * documents, which on the wire is identical to a space that genuinely has
   * no tracks.
   *
   * So a caller-supplied constraint here would not fail loudly. It would
   * make every borrowed track quietly disappear, with nothing in the console
   * and nothing in the network tab to distinguish it from an empty space.
   * `attach()` takes one constraint array and applies it to all three
   * collections, so a filter intended for TRACK would have silently hit the
   * rows too.
   *
   * The row query therefore ignores caller constraints and always pins its
   * own shape. Do not "fix" this by threading the argument through.
   */
  const constraintsFor = (
    collectionId: COLLECTION_ID,
    spaceId: string,
    callerConstraints?: QueryConstraint[],
  ): QueryConstraint[] => {
    const pinnedSpaceEquality = [where("spaceId", "==", spaceId)];
    if (collectionId === COLLECTION_ID.SPACE_TRACK) return pinnedSpaceEquality;
    return callerConstraints || pinnedSpaceEquality;
  };

  const manageGlobalChildEntityWatchers = useCallback(
    ({
      spaceId,
      collectionIdsToWatch = [
        COLLECTION_ID.SPACE,
        COLLECTION_ID.TRACK,
        COLLECTION_ID.SPACE_TRACK,
      ],
      componentId,
    }: {
      spaceId?: string;
      collectionIdsToWatch?: COLLECTION_ID[];
      componentId: WatcherComponentId;
    }) => {
      return {
        attach: async (constraints?: QueryConstraint[], limit?: number) => {
          if (spaceId) {
            return Promise.all(
              collectionIdsToWatch.map((collectionIdToTrack) => {
                return setGlobalChildEntityWatchers({
                  action: "attach",
                  collectionId: collectionIdToTrack,
                  parentSpaceId: spaceId,
                  constraints: constraintsFor(
                    collectionIdToTrack,
                    spaceId,
                    constraints,
                  ),
                  limit,
                  componentId,
                });
              }),
            );
          }

          return;
        },
        detach: async (constraints?: QueryConstraint[]) => {
          if (spaceId) {
            return Promise.all(
              collectionIdsToWatch.map((collectionIdToTrack) => {
                return setGlobalChildEntityWatchers({
                  action: "detach",
                  collectionId: collectionIdToTrack,
                  parentSpaceId: spaceId,
                  constraints: constraintsFor(
                    collectionIdToTrack,
                    spaceId,
                    constraints,
                  ),
                  componentId,
                });
              }),
            );
          }

          return;
        },
      };
    },
    [],
  );

  const getChildTracks = (parentSpaceId: string) => {
    const childTracks = globalTracks.childTracks.get(parentSpaceId) || [];
    // `childTracks` (the union of the legacy per-space query and
    // space-tracks-v1 rows — see `useGlobalTracks`) can hold ids for a
    // borrowed track whose canonical Track doc was never fetched by the
    // legacy query. `buildSpaceTrackTile` resolves those from their
    // space-tracks-v1 row instead — a row-backed tile needs no canonical
    // Track doc at all. Only an id backed by NEITHER a row NOR a resolved
    // Track (unreachable today given how childTracks membership is
    // populated, but defended anyway) still returns `undefined` and is
    // filtered here, rather than let it reach callers — several of them
    // (e.g. `formatTrackToBaseEntityRow`) dereference fields on the track
    // unconditionally.
    return childTracks
      .map((childTrackId) =>
        buildSpaceTrackTile({
          row: globalSpaceTracks.spaceTrackRows.get(
            getSpaceTrackRowId(parentSpaceId, childTrackId),
          ),
          track: globalTracks.tracks.get(childTrackId),
          // Minted, not route-derived: this is the space whose
          // `space-tracks-v1` rows produced this tile (same id as the
          // row lookup above), so by construction it IS the space the
          // tile is displayed under. For a nested space that is more
          // precise than `useUrlContext`, which resolves only the
          // route's top-level space.
          viewingSpaceId: asViewingSpaceId(parentSpaceId),
        }),
      )
      .filter((tile): tile is SpaceTrackTile => Boolean(tile));
  };

  const getChildSpaces = (parentSpaceId: string) => {
    const childSpaces = globalSpaces.childSpaces.get(parentSpaceId) || [];
    return childSpaces.map((childSpaceId) =>
      globalSpaces.spaces.get(childSpaceId),
    );
  };

  return {
    manageGlobalChildEntityWatchers,
    getChildTracks,
    getChildSpaces,
  };
};
